/**
 * The exact inputs this report was built from.
 *
 * THE ONE THING THAT MUST NOT HAPPEN HERE: showing today's latest setup instead
 * of the version the run was bound to. A founder who edits their setup and then
 * opens this modal on last week's report would be told the report used values it
 * never saw — and the comparison an investor draws from it would be wrong in a
 * way neither of them could detect.
 *
 * So everything rendered comes from the report's own immutable snapshot, which
 * carries its own version numbers, and the modal says which versions those are.
 */

import React from 'react';
import { Text, View } from 'react-native';
import { AppModal, ModalAction } from '@/components/modal';
import { useThemeColors } from '@/lib/useThemeColors';
import type { ProvenancedInput } from '@/lib/strategic-analysis/types';
import {
  FIELD_GROUP_LABELS,
  ORIGIN_LABELS,
  VERIFICATION_LABELS,
  VISIBILITY_LABELS,
  groupFields,
  labelForField,
  describeInputValue,
} from './field-groups';

export interface InputsUsedSnapshot {
  strategyContextVersion: number;
  strategyContextVersionId: string;
  marketAssumptionVersion?: number | null;
  exitThesisVersion?: number | null;
  methodologyVersion: string;
  generatedAt?: string | null;
  context: Record<string, ProvenancedInput>;
}

interface InputsUsedModalProps {
  visible: boolean;
  onClose: () => void;
  snapshot: InputsUsedSnapshot | null;
  /** Opens the guarded source action. Absent when the viewer cannot inspect. */
  onInspectSource?: (fieldKey: string, sourceKey: string) => void;
}

export function InputsUsedModal({ visible, onClose, snapshot, onInspectSource }: InputsUsedModalProps) {
  const colors = useThemeColors();
  const groups = snapshot ? groupFields(Object.keys(snapshot.context)) : [];

  return (
    <AppModal
      visible={visible}
      onClose={onClose}
      title="Inputs used"
      footer={
        <AppModal.Actions>
          <ModalAction label="Close" onPress={onClose} variant="neutral" />
        </AppModal.Actions>
      }
    >
      {!snapshot
        ? (
          <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
            This analysis has not recorded its inputs yet.
          </Text>
        )
        : (
          <View className="gap-5">
            <View className="rounded-xl p-3.5 gap-1" style={{ backgroundColor: colors.surface.secondary }}>
              <Text className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                Analysis setup v{snapshot.strategyContextVersion}
              </Text>
              {typeof snapshot.marketAssumptionVersion === 'number' && (
                <Text className="text-sm" style={{ color: colors.text.secondary }}>
                  Market assumptions v{snapshot.marketAssumptionVersion}
                </Text>
              )}
              {typeof snapshot.exitThesisVersion === 'number' && (
                <Text className="text-sm" style={{ color: colors.text.secondary }}>
                  Founder exit thesis v{snapshot.exitThesisVersion}
                </Text>
              )}
              <Text className="text-sm" style={{ color: colors.text.secondary }}>
                Methodology v{snapshot.methodologyVersion}
              </Text>
              {snapshot.generatedAt && (
                <Text className="text-sm" style={{ color: colors.text.secondary }}>
                  Generated {new Date(snapshot.generatedAt).toLocaleString()}
                </Text>
              )}
              <Text className="text-xs leading-4 mt-1" style={{ color: colors.text.tertiary }}>
                These are the versions this analysis read. Later edits create new versions and do not
                change what is shown here.
              </Text>
            </View>

            {groups.map((group) => (
              <View key={group.id} className="gap-2.5">
                <Text className="text-xs font-semibold uppercase" style={{ color: colors.text.tertiary }}>
                  {FIELD_GROUP_LABELS[group.id]}
                </Text>
                {group.fields.map((fieldKey) => {
                  const input = snapshot.context[fieldKey];
                  return (
                    <View
                      key={fieldKey}
                      className="rounded-xl p-3 gap-2"
                      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light, overflow: 'hidden' }}
                    >
                      <Text className="text-xs font-semibold" style={{ color: colors.text.tertiary }}>
                        {labelForField(fieldKey)}
                      </Text>
                      <Text className="text-sm leading-5" style={{ color: colors.text.primary }}>
                        {describeInputValue(input)}
                      </Text>
                      <View className="flex-row flex-wrap gap-1.5">
                        <Chip label={ORIGIN_LABELS[input.originLabel]} colors={colors} />
                        <Chip label={VERIFICATION_LABELS[input.verificationLabel]} colors={colors} />
                        <Chip label={VISIBILITY_LABELS[input.publicationScope]} colors={colors} />
                        {input.conflictDisposition === 'resolved_by_founder' && (
                          <Chip label="Conflict resolved by founder" colors={colors} />
                        )}
                      </View>
                      {/* The handle is opaque. Resolving it is a server action
                          that rechecks the caller's access to the underlying
                          source, so an investor without data-room access gets
                          `source_detail_unavailable` rather than an excerpt. */}
                      {input.detailAvailable && onInspectSource && input.sourceKeys[0] && (
                        <Text
                          accessibilityRole="button"
                          onPress={() => onInspectSource(fieldKey, input.sourceKeys[0])}
                          className="text-sm font-semibold"
                          style={{ color: colors.primary[700] }}
                        >
                          View source
                        </Text>
                      )}
                    </View>
                  );
                })}
              </View>
            ))}
          </View>
        )}
    </AppModal>
  );
}

function Chip({ label, colors }: { label: string; colors: ReturnType<typeof useThemeColors> }) {
  return (
    <View className="rounded-full px-2 py-1" style={{ backgroundColor: colors.surface.secondary }}>
      <Text numberOfLines={1} className="text-xs" style={{ color: colors.text.secondary }}>
        {label}
      </Text>
    </View>
  );
}
